import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Col, Row, message, Typography, Skeleton, Table, notification } from "antd";

import { getEvaluationsDeleted, getEvaluationsDeletedByCode } from "../../../../API/evaluation";

import WithPermission from "../../../Common/WithPermission/WithPermission";
import ExportButton from '../../../Common/ExportButton/ExportButton';
import BackButton from "../../../Common/BackButton/BackButton";
import BreadCrumb from "../../../Common/BreadCrumb/BreadCrumb";
import PageTitle from "../../../Common/PageTitle/PageTitle";
import ChangeLog from "../../../Common/ChangeLog/ChangeLog";
import EvaluationDeletedSearch from "./EvaluationDeletedSearch";
import Columns from "./columns";

import { useAuth } from "../../../Context/AuthContext";

const EvaluationDeleted = () => {
  const navigate = useNavigate();
  // Permisos
  const { hasPermission, userData } = useAuth();
  // Consulta permisos: Superusuario  
  const isSuperuser = userData?.is_superuser === true;
  // Notificaciones
  const [api, contextHolder] = notification.useNotification();
  // Data
  const [evaluationDeleted, setEvaluationDeleted] = useState(null);
  // Buscador
  const [filteredData, setFilteredData] = useState([]); 
  const [searchText, setSearchText] = useState(''); 

  // Obtener filtros desde la URL (si vienen desde el dashboard o la vista original)
  const [searchParams] = useSearchParams();
  const assetsFromUrl = searchParams.get('assets')?.split(',').filter(Boolean) || [];
  const areasFromUrl = searchParams.get('areas')?.split(',').filter(Boolean) || [];

  useEffect(() => {
    const fetchEvaluationDeleted = async () => {
      try {
        const data = await getEvaluationsDeleted();
        setEvaluationDeleted(data);
        // Apply URL filters if provided
        let filtered = data;
        if (assetsFromUrl.length) {
          filtered = filtered.filter(item => {
            const assetName = item.dependency_control?.dependency?.asset?.name;
            return assetName ? assetsFromUrl.includes(assetName) : false;
          });
        }
        if (areasFromUrl.length) {
          filtered = filtered.filter(item => {
            const areaName = item.area?.name;
            return areaName ? areasFromUrl.includes(areaName) : false;
          });
        }
        setFilteredData(filtered);
      } catch (error) {
        message.error("No se encontraron las evaluaciones eliminados.");
      }
    };

    fetchEvaluationDeleted();
  }, []);

  const filterParams = new URLSearchParams();
  if (assetsFromUrl.length) filterParams.set('assets', assetsFromUrl.join(','));
  if (areasFromUrl.length) filterParams.set('areas', areasFromUrl.join(','));

  const breadCrumbUrl = [
    { label: "Evaluaciones", url: "/evaluation", search: filterParams.toString() ? `?${filterParams.toString()}` : '' },
    { label: "Eliminadas" },
  ];

  const goToBioFunctions = () => {
    navigate(-1);
  };

  const [changeLogVisible, setChangeLogVisible] = useState(false);
  const [changeLogData, setChangeLogData] = useState(null);
  const [changeLogLogs, setChangeLogLogs] = useState([]);

  const handleOpenChangeLog = async (record) => {
    try {
      const evaluation = await getEvaluationsDeletedByCode(record.code);
      setChangeLogData(record);
      setChangeLogLogs(evaluation.logs || []);
      setChangeLogVisible(true); 
    } catch (error) {
      api.error({ message: "Error al obtener logs", description: error.message });
    }
  };

  const handleCloseChangeLog = () => {
    setChangeLogVisible(false);
    setTimeout(() => {
      setChangeLogData(null);
      setChangeLogLogs([]);
    }, 300);
  };

  const evaluationDeletedTableFilter = () => {
    const uniqueAssets = Array.from(
      new Set(
        evaluationDeleted
          .map(c => c.dependency_control?.dependency?.asset?.name)
          .filter(Boolean)
      )
    ).sort();

    const uniqueArea = Array.from(
      new Map(
        evaluationDeleted
          .filter(c => c.area)
          .map(c => [c.area.id, c.area])
      ).values()
    ).sort((a, b) => a.name.localeCompare(b.name));

    return {
      assetFilterOptions: uniqueAssets,
      areaFilterOptions: uniqueArea,
      dashboardAssetsFilter: assetsFromUrl ?? [],
      dashboardAreasFilter: areasFromUrl ?? [],
    };
  };

  return (
    <WithPermission
      isSuperuser={isSuperuser}
      //hasAnyRole={hasAnyRole}
      requiredPermission="read"
      //currentPermission={canAssetsRead}
      redirectTo="/home"
    >
      <>
        {contextHolder}
        <div className="button-container">
          <Row justify="space-between">
            <Col span={18}>
              <PageTitle title="Evaluaciones Eliminadas" icon="tasks" />
            </Col>
          </Row>

          <Row justify="start" align="top">
            <Col span={18} style={{ display: "flex", alignItems: "center" }}>
              <BackButton onConfirm={goToBioFunctions} />
              <BreadCrumb location={breadCrumbUrl} />
            </Col>
            <Col span={6} style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <EvaluationDeletedSearch
                evaluationData={evaluationDeleted}
                searchText={searchText}
                setSearchText={setSearchText}
                setFilteredData={setFilteredData}
              />
            </Col>
          </Row>

          <Row justify="end">
            <Col span={6} offset={18} style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <ExportButton
                api={api}
                contextHolder={contextHolder}
                data={filteredData}
                filename="Activos Eliminados.csv"
                transformData={(data) =>
                  data.map(item => ({
                    Nombre: item.name,
                    Descripción: item.description,
                    Responsable: item.area?.name || 'N/A',
                    "Creado por": item.user?.username || 'N/A',
                  }))
                }
              />
            </Col>
          </Row>
        </div>
      {
        !evaluationDeleted ? (
        <>
          <Skeleton active paragraph={{ rows: 6 }} />
          <div style={{ height: 32 }} />
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Skeleton active paragraph={{ rows: 6 }} />
            </Col>
            <Col xs={24} md={12}>
              <Skeleton active paragraph={{ rows: 6 }} />
            </Col>
          </Row>
        </>
        ) : (
          <>
            <Table
              className="custom-pagination deleted-table "
              columns={Columns(handleOpenChangeLog, evaluationDeletedTableFilter())}
              dataSource={filteredData} 
              rowKey="code"
              scroll={{ y: 450 }}
              pagination={{
                defaultPageSize: 30,
                showSizeChanger: true,
                pageSizeOptions: ["30", "50", "100"],
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} de ${total} registros`,
              }}
            />

            <ChangeLog
              open={changeLogVisible}
              onClose={handleCloseChangeLog}
              data={changeLogData}
              logs={changeLogLogs}
            />
          </>
        )
      }
      </>
    </WithPermission>
  );
};

export default EvaluationDeleted;